/**
 * Runtime validation for the itinerary document.
 *
 * Hand-rolled (no schema library) to keep the bundle tiny. It throws a
 * descriptive `Error` on the first problem it finds, so a malformed
 * `itinerary.json` fails loudly at load time rather than rendering a broken map.
 */

import {
  CATEGORIES,
  type Category,
  type Day,
  type Hotel,
  type Itinerary,
  type Stop,
  type TripMeta,
} from './types';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function fail(path: string, message: string): never {
  throw new Error(`Invalid itinerary at ${path}: ${message}`);
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function str(v: unknown, path: string): string {
  if (typeof v !== 'string' || v.length === 0) fail(path, 'expected a non-empty string');
  return v as string;
}

function optionalStr(v: unknown, path: string): string | undefined {
  if (v === undefined) return undefined;
  if (typeof v !== 'string') fail(path, 'expected a string');
  return v;
}

function isoDate(v: unknown, path: string): string {
  const s = str(v, path);
  if (!ISO_DATE.test(s)) fail(path, `expected an ISO date (YYYY-MM-DD), got "${s}"`);
  // Reject shape-valid but impossible dates like "2026-13-45": reconstruct and
  // require the components to round-trip.
  const y = Number(s.slice(0, 4));
  const mo = Number(s.slice(5, 7));
  const d = Number(s.slice(8, 10));
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) {
    fail(path, `not a real calendar date: "${s}"`);
  }
  return s;
}

function category(v: unknown, path: string): Category {
  if (typeof v !== 'string' || !CATEGORIES.includes(v as Category)) {
    fail(path, `expected category one of ${CATEGORIES.join(', ')}, got "${String(v)}"`);
  }
  return v as Category;
}

function lat(v: unknown, path: string): number {
  if (typeof v !== 'number' || Number.isNaN(v) || v < -90 || v > 90) {
    fail(path, `lat must be a number in [-90, 90], got ${String(v)}`);
  }
  return v;
}

function lng(v: unknown, path: string): number {
  if (typeof v !== 'number' || Number.isNaN(v) || v < -180 || v > 180) {
    fail(path, `lng must be a number in [-180, 180], got ${String(v)}`);
  }
  return v;
}

function validateStop(v: unknown, path: string): Stop {
  if (!isObject(v)) fail(path, 'expected an object');
  return {
    name: str(v.name, `${path}.name`),
    category: category(v.category, `${path}.category`),
    time: optionalStr(v.time, `${path}.time`),
    lat: lat(v.lat, `${path}.lat`),
    lng: lng(v.lng, `${path}.lng`),
    placeId: optionalStr(v.placeId, `${path}.placeId`),
    url: optionalStr(v.url, `${path}.url`),
    note: optionalStr(v.note, `${path}.note`),
  };
}

function validateDay(v: unknown, path: string): Day {
  if (!isObject(v)) fail(path, 'expected an object');
  if (typeof v.n !== 'number' || !Number.isInteger(v.n) || v.n < 1) {
    fail(`${path}.n`, 'expected a positive integer day number');
  }
  if (!Array.isArray(v.stops)) fail(`${path}.stops`, 'expected an array');
  return {
    n: v.n,
    date: isoDate(v.date, `${path}.date`),
    dow: str(v.dow, `${path}.dow`),
    title: str(v.title, `${path}.title`),
    category: category(v.category, `${path}.category`),
    stops: v.stops.map((s, i) => validateStop(s, `${path}.stops[${i}]`)),
  };
}

function validateHotel(v: unknown, path: string): Hotel {
  if (!isObject(v)) fail(path, 'expected an object');
  return {
    name: str(v.name, `${path}.name`),
    nights: str(v.nights, `${path}.nights`),
    lat: lat(v.lat, `${path}.lat`),
    lng: lng(v.lng, `${path}.lng`),
    placeId: optionalStr(v.placeId, `${path}.placeId`),
    note: optionalStr(v.note, `${path}.note`),
  };
}

function validateTrip(v: unknown, path: string): TripMeta {
  if (!isObject(v)) fail(path, 'expected an object');
  if (!isObject(v.flights)) fail(`${path}.flights`, 'expected an object');
  // Flights are informational; validate leniently but keep the shape.
  return {
    title: str(v.title, `${path}.title`),
    start: isoDate(v.start, `${path}.start`),
    end: isoDate(v.end, `${path}.end`),
    flights: v.flights as TripMeta['flights'],
  };
}

/**
 * Validate an unknown value and return it as a typed `Itinerary`.
 * Throws an `Error` describing the first problem encountered.
 */
export function validateItinerary(data: unknown): Itinerary {
  if (!isObject(data)) fail('$', 'expected an object');
  if (!Array.isArray(data.days)) fail('$.days', 'expected an array of days');
  if (!Array.isArray(data.hotels)) fail('$.hotels', 'expected an array of hotels');
  return {
    trip: validateTrip(data.trip, '$.trip'),
    hotels: data.hotels.map((h, i) => validateHotel(h, `$.hotels[${i}]`)),
    days: data.days.map((d, i) => validateDay(d, `$.days[${i}]`)),
  };
}

/** Non-throwing type guard built on {@link validateItinerary}. */
export function isItinerary(data: unknown): data is Itinerary {
  try {
    validateItinerary(data);
    return true;
  } catch {
    return false;
  }
}
